import { zodResolver } from '@hookform/resolvers/zod';
import type { DebtDetail, InstallmentPlanRequest } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { type FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, ListGroup } from '../components/ui/card';
import { Segmented } from '../components/ui/segmented';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { todayIn } from '../lib/dates';
import { useDebt } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { groupAmountInput } from '../lib/money';
import { type PlanFormContext, type PlanFormValues, planFormSchema } from '../lib/plan-form';

/** Set up or change a debt's installment plan. The app never pays an installment on its own. */
export function PlanPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const me = useMe();
  const debt = useDebt(id);
  if (!me.data || debt.isPending) return <p role="status">{t('app.loading')}</p>;
  if (debt.isError) {
    return (
      <Card role="alert">
        <p>{t('errors.debt.not_found')}</p>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('debts.planTitle')} back={`/debts/${id}`} />
      <PlanForm debt={debt.data} today={todayIn(me.data.family.timezone)} />
    </div>
  );
}

function PlanForm({ debt, today }: { debt: DebtDetail; today: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const plan = debt.plan;
  const currency = debt.currency;
  // A change continues from the next open installment's month unless it is moved.
  const defaultStartMonth = plan ? (debt.nextInstallment?.dueOn.slice(0, 7) ?? null) : null;
  const ctx: PlanFormContext = { currency, defaultStartMonth };
  const openLeft = plan
    ? plan.installments.filter((i) => i.state !== 'paid' && i.state !== 'skipped').length
    : 0;
  const form = useForm<PlanFormValues, unknown, InstallmentPlanRequest>({
    resolver: zodResolver(planFormSchema(ctx)),
    defaultValues: {
      amount: plan ? groupAmountInput(plan.amount, currency) : '',
      dayOfMonth: String(plan?.dayOfMonth ?? Number.parseInt(today.slice(8), 10)),
      startMonth: defaultStartMonth ?? today.slice(0, 7),
      mode: plan && plan.count === null ? 'untilPaid' : 'count',
      count: plan && plan.count !== null && openLeft > 0 ? String(openLeft) : '',
    },
  });
  const v = useWatch({ control: form.control }) as PlanFormValues;
  const save = useMutation({
    mutationFn: (body: InstallmentPlanRequest) => api.debts.setPlan(debt.id, body),
    onSuccess: async () => {
      await invalidate();
      navigate(`/debts/${debt.id}`, { replace: true });
    },
  });
  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency }) : null;
  const inputClass =
    'min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] tabular-nums placeholder:text-muted focus-visible:outline-none';

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => save.mutate(body))}
    >
      <p className="px-1 text-sm text-muted">
        {plan ? t('debts.planChangeHint') : t('debts.existingPlanHint')}
      </p>
      <ListGroup>
        <Row label={t('debts.planAmount')} htmlFor="plan-amount" error={errorText(errors.amount)}>
          <span className="flex items-center gap-2">
            <input
              id="plan-amount"
              inputMode={currency === 'LBP' ? 'text' : 'decimal'}
              autoComplete="off"
              dir="ltr"
              placeholder={currency === 'LBP' ? '500k' : '0.00'}
              aria-invalid={errors.amount ? true : undefined}
              className={inputClass}
              {...form.register('amount')}
            />
            <span className="text-sm font-semibold">{currency}</span>
          </span>
        </Row>
        <Row label={t('debts.dayOfMonth')} htmlFor="plan-day" error={errorText(errors.dayOfMonth)}>
          <input
            id="plan-day"
            inputMode="numeric"
            autoComplete="off"
            dir="ltr"
            maxLength={2}
            aria-invalid={errors.dayOfMonth ? true : undefined}
            className={inputClass}
            {...form.register('dayOfMonth')}
          />
        </Row>
        <Row
          label={t('debts.startMonth')}
          htmlFor="plan-start"
          error={errorText(errors.startMonth)}
        >
          <input
            id="plan-start"
            type="month"
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('startMonth')}
          />
        </Row>
      </ListGroup>
      <p className="px-1 text-xs text-muted">{t('debts.planAmountHint')}</p>
      <p className="px-1 text-xs text-muted">{t('debts.dayOfMonthHint')}</p>

      <Segmented
        label={t('debts.howLong')}
        options={[
          { value: 'count', label: t('debts.fixedCount') },
          { value: 'untilPaid', label: t('debts.untilPaid') },
        ]}
        value={v.mode}
        onChange={(mode) => form.setValue('mode', mode)}
      />
      {v.mode === 'count' && (
        <ListGroup>
          <Row label={t('debts.count')} htmlFor="plan-count" error={errorText(errors.count)}>
            <input
              id="plan-count"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              maxLength={3}
              aria-invalid={errors.count ? true : undefined}
              className={inputClass}
              {...form.register('count')}
            />
          </Row>
        </ListGroup>
      )}

      <FormError error={save.error} />
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : t('debts.savePlan')}
      </Button>
    </form>
  );
}
